import { Grid, Typography } from '@mui/material'
import { GetStaticProps } from 'next'

import { Faq } from '@ecm/data/Faq'
import { runEff } from '@ecm/effect'
import FaqBlock from '@ecm/ui/component/block/Faq'
import NewsletterSignupCta from '@ecm/ui/component/block/NewsletterSignupCta'
import OurCustomersBlock from '@ecm/ui/component/block/OurCustomers'
import PlainHeader from '@ecm/ui/component/block/PlainHeader'
import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import DefaultLayout from '@ecm/ui/layout/Default'

import actionsIllustration from '/public/images/illustration/actions.svg'
import mapWithPinIllustration from '/public/images/illustration/map-with-pin.svg'
import newMailIllustration from '/public/images/illustration/new-mail.svg'
import secureFacilityIllustration from '/public/images/illustration/secure-facility.svg'

const blockData = [
  {
    image: mapWithPinIllustration,
    paragraphs: [
      'Transform your business with our suite of products focused on providing digital solutions for your postal mail, checks, and important documents.',
    ],
    title: 'Pick an address in a city of your choice',
  },
  {
    image: secureFacilityIllustration,
    paragraphs: [
      'Never touch, sort, or stack the office mail again! Every piece of mail you receive will be logged, organized, and stored securely by one of our trained employees.',
    ],
    title: 'Your mail routes to our secure facilities',
  },
  {
    image: newMailIllustration,
    paragraphs: [
      'When it’s time to check your mail, you can log into your Earth Class account from your computer or smartphone.',
      'Here, you can review the front and back of each piece of mail as a high resolution PDF document with searchable text.',
    ],
    title: 'Check your mail',
  },
  {
    image: actionsIllustration,
    paragraphs: [
      'You get to decide what actions to take on your mail.',
      'You can trash and recycle junk mail, deposit checks, download important documents to save, or forward to your attorney, accountant or colleague.',
    ],
    title: 'Take action',
  },
]

type HowItWorksPageProps = {
  faqs: Faq[]
}

const HowItWorksPage = ({ faqs }: HowItWorksPageProps) => (
  <DefaultLayout seoPage="how-virtual-mail-works">
    <PlainHeader title="How Virtual Mail Works" />
    <MainContainer sx={{ py: '4em' }}>
      {blockData.map(({ image, title, paragraphs }, index) => (
        <Grid
          key={title}
          container
          flexWrap="wrap"
          alignItems="center"
          justifyContent="center"
          borderBottom={`1px solid ${Color.smoke.normal}`}
          flexDirection={index % 2 === 0 ? 'row' : 'row-reverse'}
          py="2em"
          marginBottom="2em"
        >
          <Grid item xs={12} sm={6} md={5} lg={5} px={{ sm: '2em', xs: 0 }}>
            <Typography variant="h3" component="h2">
              {title}
            </Typography>
            {paragraphs.map((p, i) => (
              <Typography variant="p2" key={i}>
                {p}
              </Typography>
            ))}
          </Grid>
          <Grid item xs={8} sm={6} md={5} lg={4} px={{ sm: '2em', xs: 0 }}>
            <CloudflareImage src={image} />
          </Grid>
        </Grid>
      ))}

      <NewsletterSignupCta />
    </MainContainer>

    <FaqBlock showFinePrint faqs={faqs} />

    <OurCustomersBlock />
  </DefaultLayout>
)

export default HowItWorksPage

export const getStaticProps: GetStaticProps = async () => {
  const faqs = await runEff(eff => eff.getFaqsByPage(['/how-virtual-mail-woks']))

  return { props: { faqs } }
}
